import React, { useEffect, useState } from 'react';
import {
  Grid,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { GetOrganisationsForReportAsync } from '../services/organisations';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { ColumnMenu } from '../components/columnMenu';
import Toolbar from '../components/page-toolbar';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { NoRecords } from '../components/grid/no-records';
import { DefaultGridSettings } from '../constants/grid-settings';
import { appStore } from '../stores/app-store';
import {
  CardViewDataStateChangeEvent,
  NetworkOrganisationsCard
} from './network-organisations-card';
import HelpSupport from '../components/help-support';
import { useGetAllNetworkOrganisations } from '../hooks/services/use-organisarions';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const NetworkOrganisationsGrid = () => {
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');

  const { data, isLoading, mutate } = useGetAllNetworkOrganisations(
    toDataSourceRequestString(dataState)
  );

  const reportExportRef = React.useRef<ExcelExport | null>(null);

  const onExportExcel = async () => {
    appStore.showLoading();
    try {
      await GetOrganisationsForReportAsync().then((response) => {
        if (reportExportRef.current !== null) {
          reportExportRef.current.save(response);
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      appStore.hideLoading();
    }
  };

  const TruncatedCellWithTooltip = (props: any) => {
    const { dataItem, field } = props;
    const cellValue = dataItem[field];

    return (
      <td
        title={cellValue}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '150px'
        }}>
        {cellValue}
      </td>
    );
  };

  useEffect(() => {
    mutate();
  }, [dataState]);

  return (
    <>
      <Toolbar title={'Organisations'}>
        <HelpSupport
          title="My network guide"
          url="https://forum.catalystglobal.com/t//4215"
        />
      </Toolbar>
      <ExcelExport ref={reportExportRef} fileName={'OrganisationsReport.xlsx'}>
        <ExcelExportColumn field="name" title="Name" width={300} />
        <ExcelExportColumn field="email" title="Email" width={200} />
        <ExcelExportColumn field="phoneNumber" title="Phone" width={200} />
        <ExcelExportColumn field="admins" title="Admin" width={350} />
        <ExcelExportColumn field="territory" title="Territory(s)" width={350} />
        <ExcelExportColumn field="languages" title="Language" width={200} />
        <ExcelExportColumn field="website" title="Website" width={200} />
      </ExcelExport>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search organisations'}
          columnsToSearch={['name']}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          exportRef={reportExportRef}
          useCustomExportExcel={true}
          handleCustomExportToExcel={onExportExcel}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        {gridMode === 'grid' && (
          <Grid
            id={'grid-organisations'}
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            data={data}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column field={'name'} title={'Name'} columnMenu={ColumnMenu} />
            <Column field={'email'} title={'Email'} columnMenu={ColumnMenu} />
            <Column
              field={'phoneNumber'}
              title={'Phone'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'admins'}
              title={'Admin'}
              cells={{ data: TruncatedCellWithTooltip }}
            />
            <Column
              field={'territory'}
              title={'Territory(s)'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'languages'}
              title={'Language'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'website'}
              title={'Website'}
              columnMenu={ColumnMenu}
            />

            <GridNoRecords>
              {isLoading ? (
                <div
                  className={
                    'd-flex flex-column align-items-center gap-2 text-muted'
                  }>
                  <Loader type={'pulsing'} />
                  <span>Loading data</span>
                </div>
              ) : (
                <NoRecords />
              )}
            </GridNoRecords>
          </Grid>
        )}
        {gridMode === 'card' && (
          <NetworkOrganisationsCard
            data={data?.data ?? []}
            total={data?.total}
            {...dataState}
            onDataStateChange={(e: CardViewDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
          />
        )}
      </div>
    </>
  );
};
export default NetworkOrganisationsGrid;
